import { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';

/**
 * Tower 12 override of the rise VirtualTourEmbed.
 *
 * The property's `property_font_color` is white, which the runtime maps to
 * `--vibe-color-text`. The tour selector sits on a white field, so its text
 * is pinned to a fixed dark color instead of the theme text token.
 */

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white py-8 md:py-12">
      {/* Tour selector */}
      {hasMultiple && (
        <div className="flex justify-end max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <select
            value={selectedIndex}
            onChange={(e) => setSelectedIndex(Number(e.target.value))}
            style={{ color: '#1a1a1a', backgroundColor: '#ffffff', borderColor: '#000000' }}
            className="font-body text-sm bg-white text-[#1a1a1a] border border-black rounded px-3 py-2 focus:outline-none focus:border-[var(--vibe-color-primary)] max-w-full truncate"
          >
            {validTours.map((tour, i) => (
              <option key={i} value={i} style={{ color: '#1a1a1a', backgroundColor: '#ffffff' }}>
                {tour.virtual_tour_title || `Tour ${i + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tour embed */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTour?.virtual_tour_url ? (
          <div className="relative w-full rounded-lg overflow-hidden border border-[var(--vibe-color-border)]" style={{ paddingBottom: '56.25%' }}>
            <iframe
              key={activeTour.virtual_tour_url}
              src={activeTour.virtual_tour_url}
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
              title={activeTour.virtual_tour_title || 'Virtual Tour'}
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
